import {
  ColumnVisibility,
  DataTableColumn,
  StorageConfig,
} from './DataTable.types'

// Helper to get from localStorage with fallback
export const getStoredValue = <T>(key: string, defaultValue: T): T => {
  if (typeof window === 'undefined') return defaultValue

  try {
    const stored = window.localStorage.getItem(key)
    return stored ? JSON.parse(stored) : defaultValue
  } catch {
    return defaultValue
  }
}

// Helper to save to localStorage
export const storeValue = (key: string, value: any): void => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error)
  }
}

// Get storage keys for a table
export const getStorageKeys = (storageKey: string): StorageConfig => ({
  visibility: `${storageKey}_column_visibility`,
  widths: `${storageKey}_column_widths`,
  order: `${storageKey}_column_order`,
})

// Initialize column visibility from columns definition
export const getDefaultColumnVisibility = <T>(
  columns: DataTableColumn<T>[]
): ColumnVisibility => {
  return columns.reduce((acc, column) => {
    acc[column.key] = true
    return acc
  }, {} as ColumnVisibility)
}

// Initialize column order from columns definition
export const getDefaultColumnOrder = <T>(
  columns: DataTableColumn<T>[]
): string[] => {
  return columns.map((column) => column.key)
}

// Get columns in order with visibility and custom widths applied
export const getOrderedVisibleColumns = <T>(
  columns: DataTableColumn<T>[],
  columnOrder: string[],
  columnVisibility: ColumnVisibility,
  columnWidths: Record<string, number>
): DataTableColumn<T>[] => {
  const columnMap = new Map(columns.map((col) => [col.key, col]))

  const visibleColumns: DataTableColumn<T>[] = []
  const seen = new Set<string>()

  // A column counts as visible unless it's *explicitly* hidden. A missing
  // entry defaults to visible — this MUST match the show/hide panel, which
  // renders its checkbox as `columnVisibility[key] ?? true`. Treating a
  // missing entry as hidden here (the old behaviour) desynced the two: the
  // panel showed the column ticked while the table dropped it.
  // Columns flagged `hideable: false` always render, ignoring any stored
  // (even explicitly-false) visibility — the panel locks them on too.
  const isVisible = (key: string): boolean => {
    if (columnMap.get(key)?.hideable === false) return true
    return columnVisibility[key] ?? true
  }

  const pushColumn = (key: string): void => {
    const column = columnMap.get(key)
    if (!column || seen.has(key) || !isVisible(key)) return
    seen.add(key)
    visibleColumns.push({
      ...column,
      width: columnWidths[key] ? `${columnWidths[key]}px` : column.width,
    })
  }

  for (const key of columnOrder) pushColumn(key)
  // Append any column absent from `columnOrder` (e.g. added after the order
  // was persisted to storage) instead of dropping it. The panel lists every
  // column, so the table must render every visible one too.
  for (const column of columns) pushColumn(column.key)

  return visibleColumns
}

// Format number for display with locale
export const formatNumber = (value: number, locale = 'en-US'): string => {
  return new Intl.NumberFormat(locale).format(value)
}

// Default empty state component
export const getDefaultEmptyState = () => ({
  title: 'No Data Found',
  description: 'Try adjusting your filters or search criteria',
})

// Default error state component
export const getDefaultErrorState = (error: string) => ({
  title: 'Error Loading Data',
  description: error,
})

// Get sort class for header
export const getSortClass = (
  field: string,
  sort: { field: string; order: 'asc' | 'desc' }[]
): string => {
  const sortItem = sort.find((s) => s.field === field)
  if (!sortItem) return ''
  return sortItem.order === 'asc' ? 'sort-asc' : 'sort-desc'
}

// Calculate pagination info
export const getPaginationInfo = (
  currentPage: number,
  pageSize: number,
  total: number
) => {
  // Safeguard against undefined/NaN values
  const safePage = currentPage || 1
  const safePageSize = pageSize || 20
  const safeTotal = total || 0

  const totalPages = Math.ceil(safeTotal / safePageSize) || 1
  const startItem = safeTotal === 0 ? 0 : (safePage - 1) * safePageSize + 1
  const endItem = Math.min(safePage * safePageSize, safeTotal)

  return {
    totalPages,
    startItem,
    endItem,
  }
}

// Generate page numbers for pagination
export const getPageNumbers = (
  currentPage: number,
  totalPages: number,
  maxPages = 5
): number[] => {
  const pages: number[] = []
  const startPage = Math.max(
    1,
    Math.min(totalPages - maxPages + 1, currentPage - Math.floor(maxPages / 2))
  )

  for (let i = 0; i < Math.min(maxPages, totalPages); i++) {
    const pageNum = startPage + i
    if (pageNum <= totalPages) {
      pages.push(pageNum)
    }
  }

  return pages
}
